import { z } from 'zod';

import { CS_DRAFT_BODY_MAX_LENGTH } from '../agents/outcomes';
import { PageQuerySchema, SearchQuerySchema } from '../shared/pagination';
import { type Channel, ChannelSchema } from './channel-connection';
import { InboxIdentitySchema } from './inbox-identity';
import { type InboxSurface, InboxSurfaceSchema } from './inbox-surface';
import { SmsKeywordSchema } from './klaviyo-sms';

export const COMMENT_HIDING_CHANNELS = [
  'instagram',
  'facebook',
  'tiktok',
] as const satisfies readonly Channel[];

export function channelHidesComments(channel: Channel): boolean {
  return COMMENT_HIDING_CHANNELS.some((hiding) => hiding === channel);
}

export const INBOX_REPLY_OFF_CHANNELS = ['facebook_group'] as const satisfies readonly Channel[];

export function channelRepliesFromInbox(channel: Channel): boolean {
  return !INBOX_REPLY_OFF_CHANNELS.some((off) => off === channel);
}

const MB = 1024 * 1024;

export const REPLY_ATTACHMENT_MAX_BYTES = 10 * MB;
export const REPLY_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
] as const;
export type ReplyAttachmentType = (typeof REPLY_ATTACHMENT_TYPES)[number];

const REPLY_ATTACHMENT_TYPE_LABELS: Record<ReplyAttachmentType, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/gif': 'GIF',
  'application/pdf': 'PDF',
};

export function isReplyAttachmentType(type: string): type is ReplyAttachmentType {
  return REPLY_ATTACHMENT_TYPES.some((known) => known === type);
}

export function isImageAttachment(type: ReplyAttachmentType): boolean {
  return type !== 'application/pdf';
}

export type ReplyAttachmentRule =
  | {
      accepts: true;
      where: string;
      maxBytes: Partial<Record<ReplyAttachmentType, number>>;
      textRequired: boolean;
      wrongTypeHint?: string;
    }
  | { accepts: false; reason: string };

const META_DM_LIMITS = {
  'image/jpeg': 8 * MB,
  'image/png': 8 * MB,
  'application/pdf': REPLY_ATTACHMENT_MAX_BYTES,
};

const COMMENT_FILE_REFUSAL = 'comments can only carry text. Send the file by DM.';

export function replyAttachmentRule(channel: Channel, surface: InboxSurface): ReplyAttachmentRule {
  switch (channel) {
    case 'instagram':
      return surface === 'dm'
        ? {
            accepts: true,
            where: 'in an Instagram DM',
            maxBytes: META_DM_LIMITS,
            textRequired: false,
          }
        : { accepts: false, reason: `Instagram ${COMMENT_FILE_REFUSAL}` };
    case 'facebook':
      return surface === 'dm'
        ? {
            accepts: true,
            where: 'in a Facebook DM',
            maxBytes: META_DM_LIMITS,
            textRequired: false,
          }
        : {
            accepts: true,
            where: 'in a Facebook comment',
            maxBytes: { 'image/jpeg': 8 * MB, 'image/png': 8 * MB },
            textRequired: false,
            wrongTypeHint: 'Send a PDF by DM.',
          };
    case 'whatsapp':
      return {
        accepts: true,
        where: 'on WhatsApp',
        maxBytes: {
          'image/jpeg': 5 * MB,
          'image/png': 5 * MB,
          'application/pdf': REPLY_ATTACHMENT_MAX_BYTES,
        },
        textRequired: false,
      };
    case 'intercom':
      return {
        accepts: true,
        where: 'on Intercom',
        maxBytes: Object.fromEntries(
          REPLY_ATTACHMENT_TYPES.map((type) => [type, REPLY_ATTACHMENT_MAX_BYTES]),
        ),
        textRequired: true,
      };
    case 'tiktok':
      return { accepts: false, reason: `TikTok ${COMMENT_FILE_REFUSAL}` };
    case 'quo':
      return {
        accepts: false,
        reason: "Quo's API sends text only. Send the file from the Quo app.",
      };
    case 'klaviyo_sms':
      return {
        accepts: false,
        reason: "Klaviyo texts can't carry a file from here. Send it by email or DM.",
      };
    case 'facebook_group':
      return { accepts: false, reason: FACEBOOK_GROUP_REPLIES_OFF };
    default:
      return { accepts: false, reason: 'This channel cannot carry a file.' };
  }
}

export function replyAttachmentTypes(rule: ReplyAttachmentRule): ReplyAttachmentType[] {
  return rule.accepts ? REPLY_ATTACHMENT_TYPES.filter((type) => rule.maxBytes[type]) : [];
}

export function replyAttachmentRefusal(
  rule: ReplyAttachmentRule,
  file: { type: string; size: number },
): string | null {
  if (!rule.accepts) {
    return rule.reason;
  }
  const limit = isReplyAttachmentType(file.type) ? rule.maxBytes[file.type] : undefined;
  if (!isReplyAttachmentType(file.type) || limit === undefined) {
    const labels = replyAttachmentTypes(rule).map((type) => REPLY_ATTACHMENT_TYPE_LABELS[type]);
    const refusal = `Only ${listWithOr(labels)} files can be sent ${rule.where}.`;
    return rule.wrongTypeHint ? `${refusal} ${rule.wrongTypeHint}` : refusal;
  }
  if (file.size > limit) {
    const kind = isImageAttachment(file.type) ? 'Images' : 'PDFs';
    return `${kind} can be at most ${limit / MB} MB ${rule.where}.`;
  }
  return null;
}

function listWithOr(items: string[]): string {
  return items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`;
}

export const FACEBOOK_GROUP_REPLIES_OFF =
  'Replying in SBC Insiders is off. Cecelia and Umeda handle the group; the bot only takes down spam.';

export const INTERCOM_NO_TEAMMATE =
  'Your account has no Intercom teammate with the same email, so the reply cannot go out as you. Ask an admin to add you in Intercom, or reply there.';

export const INTERCOM_CONVERSATION_STATES = ['open', 'closed', 'snoozed'] as const;
export type IntercomConversationState = (typeof INTERCOM_CONVERSATION_STATES)[number];

export const INBOX_STATUSES = ['needs_reply', 'replied', 'dismissed'] as const;
export const InboxStatusSchema = z.enum(INBOX_STATUSES);
export type InboxStatus = z.infer<typeof InboxStatusSchema>;

export const INBOX_CATEGORIES = [
  'order_issue',
  'lead',
  'question',
  'praise',
  'spam',
  'other',
] as const;
export const InboxCategorySchema = z.enum(INBOX_CATEGORIES);
export type InboxCategory = z.infer<typeof InboxCategorySchema>;

export const INBOX_MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const;
export const InboxMessageDirectionSchema = z.enum(INBOX_MESSAGE_DIRECTIONS);
export type InboxMessageDirection = z.infer<typeof InboxMessageDirectionSchema>;

// Moderation is deliberately a separate axis from InboxCategory: a comment can be both an
// order issue and abusive, and one single-valued label cannot carry both.
export const MODERATION_VERDICTS = [
  'keep',
  'spam',
  'harassment',
  'hate',
  'sexual',
  'profanity',
] as const;
export const ModerationVerdictSchema = z.enum(MODERATION_VERDICTS);
export type ModerationVerdict = z.infer<typeof ModerationVerdictSchema>;

export const HIDE_VERDICTS = [
  'spam',
  'harassment',
  'hate',
  'sexual',
  'profanity',
] as const satisfies readonly ModerationVerdict[];

export function hidesComment(verdict: ModerationVerdict | null): boolean {
  return verdict !== null && HIDE_VERDICTS.some((hiding) => hiding === verdict);
}

export const INBOX_HIDE_STATES = ['pending', 'hidden', 'failed'] as const;
export const InboxHideStateSchema = z.enum(INBOX_HIDE_STATES);
export type InboxHideState = z.infer<typeof InboxHideStateSchema>;

export const INBOX_DELIVERY_STATUSES = ['sent', 'delivered', 'read', 'failed'] as const;
export const InboxDeliveryStatusSchema = z.enum(INBOX_DELIVERY_STATUSES);
export type InboxDeliveryStatus = z.infer<typeof InboxDeliveryStatusSchema>;

export const INBOX_CALL_EVENTS = ['missed_call', 'voicemail'] as const;
export const InboxCallEventSchema = z.enum(INBOX_CALL_EVENTS);
export type InboxCallEvent = z.infer<typeof InboxCallEventSchema>;

export const INBOX_MEDIA_TYPES = ['image', 'video', 'audio', 'file', 'share', 'story'] as const;
export const InboxMediaTypeSchema = z.enum(INBOX_MEDIA_TYPES);
export type InboxMediaType = z.infer<typeof InboxMediaTypeSchema>;

export const INBOX_MEDIA_COPY_STATUSES = ['stored', 'unavailable', 'too_large', 'failed'] as const;
export const InboxMediaCopyStatusSchema = z.enum(INBOX_MEDIA_COPY_STATUSES);
export type InboxMediaCopyStatus = z.infer<typeof InboxMediaCopyStatusSchema>;

export const DOWNLOAD_ONLY_CONTENT_TYPE = 'application/octet-stream';
export const INLINE_IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
] as const;

export function contentTypeEssence(contentType: string): string {
  return (contentType.split(';')[0] ?? '').trim().toLowerCase();
}

export function isInlineMediaContentType(contentType: string): boolean {
  const essence = contentTypeEssence(contentType);
  return (
    INLINE_IMAGE_CONTENT_TYPES.some((allowed) => allowed === essence) ||
    /^(video|audio)\/[a-z0-9.-]+$/.test(essence)
  );
}

export const INBOX_DELETION_SOURCES = ['unsend', 'removed', 'sweep'] as const;
export const InboxDeletionSourceSchema = z.enum(INBOX_DELETION_SOURCES);
export type InboxDeletionSource = z.infer<typeof InboxDeletionSourceSchema>;

export const INBOX_EMAIL_ADDRESS_ROLES = ['from', 'to', 'cc', 'bcc', 'reply_to'] as const;

// Intercom exposes no In-Reply-To or References, so the Message-ID, subject and addresses
// are what a later native email channel can rebuild threading from.
export const InboxEmailMetadataSchema = z.object({
  messageId: z.string().nullable(),
  subject: z.string().nullable(),
  addresses: z.array(
    z.object({
      role: z.enum(INBOX_EMAIL_ADDRESS_ROLES),
      email: z.string(),
      name: z.string().nullable(),
    }),
  ),
});
export type InboxEmailMetadata = z.infer<typeof InboxEmailMetadataSchema>;

export const InboxMessageAttachmentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mediaType: InboxMediaTypeSchema,
  copyStatus: InboxMediaCopyStatusSchema.nullable(),
  sizeBytes: z.number().int().nullable(),
});
export type InboxMessageAttachment = z.infer<typeof InboxMessageAttachmentSchema>;

export const InboxMessageSchema = z.object({
  id: z.uuid(),
  conversationId: z.uuid(),
  direction: InboxMessageDirectionSchema,
  authorUsername: z.string().nullable(),
  text: z.string(),
  quotedText: z.string().nullable(),
  attachments: z.array(InboxMessageAttachmentSchema),
  mediaType: InboxMediaTypeSchema.nullable(),
  mediaUrl: z.string().nullable(),
  mediaCopyStatus: InboxMediaCopyStatusSchema.nullable(),
  externalMessageId: z.string().nullable(),
  deliveryStatus: InboxDeliveryStatusSchema.nullable(),
  failureReason: z.string().nullable(),
  moderationVerdict: ModerationVerdictSchema.nullable(),
  hideState: InboxHideStateSchema.nullable(),
  hiddenAt: z.iso.datetime().nullable(),
  deletedAt: z.iso.datetime().nullable(),
  deletedVia: InboxDeletionSourceSchema.nullable(),
  sentByEmail: z.string().nullable(),
  automated: z.boolean(),
  callEvent: InboxCallEventSchema.nullable(),
  smsKeyword: SmsKeywordSchema.nullable(),
  sentAt: z.iso.datetime(),
});
export type InboxMessage = z.infer<typeof InboxMessageSchema>;

export const InboxConversationSchema = z.object({
  id: z.uuid(),
  channelConnectionId: z.uuid(),
  surface: InboxSurfaceSchema,
  externalThreadRef: z.string(),
  authorUsername: z.string().nullable(),
  permalink: z.string().nullable(),
  postCaption: z.string().nullable(),
  externalMediaRef: z.string().nullable(),
  postOwnerHandle: z.string().nullable(),
  subject: z.string().nullable(),
  category: InboxCategorySchema.nullable(),
  status: InboxStatusSchema,
  lastInboundAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type InboxConversation = z.infer<typeof InboxConversationSchema>;

export const InboxAssigneeSchema = z.object({
  adminUserId: z.uuid(),
  email: z.string(),
  isViewer: z.boolean(),
  assignedAt: z.iso.datetime(),
});
export type InboxAssignee = z.infer<typeof InboxAssigneeSchema>;

export const META_INBOX_LINK_SCOPES = ['thread', 'inbox'] as const;
export const MetaInboxLinkSchema = z.object({
  url: z.url(),
  scope: z.enum(META_INBOX_LINK_SCOPES),
});
export type MetaInboxLink = z.infer<typeof MetaInboxLinkSchema>;

export const INBOX_TAG_NAME_MAX_LENGTH = 100;

export const InboxTagSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type InboxTag = z.infer<typeof InboxTagSchema>;

export const InboxTagsSchema = z.object({
  tags: z.array(InboxTagSchema),
});
export type InboxTags = z.infer<typeof InboxTagsSchema>;

export const AddInboxTagSchema = z.object({
  name: z.string().trim().min(1).max(INBOX_TAG_NAME_MAX_LENGTH),
});
export type AddInboxTag = z.infer<typeof AddInboxTagSchema>;

export const INBOX_TAG_SOURCES = ['inbox', 'intercom', 'automation'] as const;
export type InboxTagSource = (typeof INBOX_TAG_SOURCES)[number];

export const InboxConversationSummarySchema = InboxConversationSchema.extend({
  channel: ChannelSchema,
  tags: z.array(InboxTagSchema),
  handle: z.string(),
  lastMessageText: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
  assignee: InboxAssigneeSchema.nullable(),
  unread: z.boolean().nullable(),
  metaInboxLink: MetaInboxLinkSchema.nullable(),
  closedRepeatCount: z.number().int().nonnegative(),
});
export type InboxConversationSummary = z.infer<typeof InboxConversationSummarySchema>;

export const INBOX_DRAFT_STATUSES = ['none', 'pending', 'ready', 'withheld', 'failed'] as const;
export const InboxDraftStatusSchema = z.enum(INBOX_DRAFT_STATUSES);
export type InboxDraftStatus = z.infer<typeof InboxDraftStatusSchema>;

export const InboxReplyDraftSchema = z.object({
  sessionId: z.uuid(),
  text: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  actionRequired: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type InboxReplyDraft = z.infer<typeof InboxReplyDraftSchema>;

export const InboxConversationDetailSchema = InboxConversationSummarySchema.extend({
  messages: z.array(InboxMessageSchema),
  draftStatus: InboxDraftStatusSchema,
  draft: InboxReplyDraftSchema.nullable(),
  draftWithheldReason: z.string().nullable(),
  identity: InboxIdentitySchema.nullable(),
  postOwnerConnected: z.boolean(),
  replyWindowEndsAt: z.iso.datetime().nullable(),
  intercomSender: z.object({ name: z.string().nullable() }).nullable(),
  intercomSnoozedUntil: z.iso.datetime().nullable(),
});
export type InboxConversationDetail = z.infer<typeof InboxConversationDetailSchema>;

export const INBOX_DRAFT_USE_OUTCOMES = ['verbatim', 'edited', 'discarded'] as const;
export const InboxDraftUseOutcomeSchema = z.enum(INBOX_DRAFT_USE_OUTCOMES);
export type InboxDraftUseOutcome = z.infer<typeof InboxDraftUseOutcomeSchema>;

export const InboxSinceSchema = z.iso.datetime({ offset: true });

export const INBOX_ASSIGNEE_FILTERS = ['me', 'none'] as const;
export const InboxAssigneeFilterSchema = z.enum(INBOX_ASSIGNEE_FILTERS);
export type InboxAssigneeFilter = z.infer<typeof InboxAssigneeFilterSchema>;

export const INBOX_CHANNEL_GROUPS = [
  { id: 'social', channels: ['instagram', 'facebook', 'facebook_group', 'tiktok'] },
  { id: 'whatsapp_white_glove', channels: ['whatsapp'] },
  { id: 'quo', channels: ['quo'] },
  { id: 'intercom_email', channels: ['intercom'] },
] as const satisfies readonly { id: string; channels: readonly Channel[] }[];
export type InboxChannelGroupId = (typeof INBOX_CHANNEL_GROUPS)[number]['id'];

const InboxChannelFilterSchema = z
  .union([ChannelSchema, z.array(ChannelSchema)])
  .transform((value) => (typeof value === 'string' ? [value] : value));

export const ListInboxConversationsQuerySchema = PageQuerySchema.merge(SearchQuerySchema).extend({
  channel: InboxChannelFilterSchema.optional(),
  surface: InboxSurfaceSchema.optional(),
  status: InboxStatusSchema.optional(),
  category: InboxCategorySchema.optional(),
  tag: z.uuid().optional(),
  since: InboxSinceSchema.optional(),
  assignee: InboxAssigneeFilterSchema.optional(),
  unread: z.stringbool().optional(),
});
export type ListInboxConversationsQuery = z.infer<typeof ListInboxConversationsQuerySchema>;

export const InboxConversationPageSchema = z.object({
  items: z.array(InboxConversationSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type InboxConversationPage = z.infer<typeof InboxConversationPageSchema>;

export const InboxChannelsSchema = z.object({
  channels: z.array(ChannelSchema),
});
export type InboxChannels = z.infer<typeof InboxChannelsSchema>;

export const InboxMacroSchema = z.object({
  id: z.string(),
  name: z.string(),
  body: z.string(),
});
export type InboxMacro = z.infer<typeof InboxMacroSchema>;

export const InboxMacrosSchema = z.object({
  macros: z.array(InboxMacroSchema),
});
export type InboxMacros = z.infer<typeof InboxMacrosSchema>;

export const INBOX_REPLY_MAX_LENGTH = 1000;
export const TIKTOK_REPLY_MAX_LENGTH = 150;
// An Intercom reply is usually an email, so it holds the drafter's whole draft.
export const INTERCOM_REPLY_MAX_LENGTH = CS_DRAFT_BODY_MAX_LENGTH;

export function replyMaxLength(channel: Channel): number {
  if (channel === 'tiktok') {
    return TIKTOK_REPLY_MAX_LENGTH;
  }
  return channel === 'intercom' ? INTERCOM_REPLY_MAX_LENGTH : INBOX_REPLY_MAX_LENGTH;
}

export const WHATSAPP_REPLY_WINDOW_MS = 24 * 60 * 60 * 1000;
export const WHATSAPP_WINDOW_CLOSED = 'WhatsApp window closed; reply by email';

export const COMMENT_DELETED_REFUSAL =
  'The commenter deleted this comment, so it can no longer get a reply.';

export function commentDeletedByAuthor(input: {
  surface: InboxSurface;
  messages: readonly { direction: InboxMessageDirection; deletedAt: Date | string | null }[];
}): boolean {
  if (input.surface !== 'comment_thread') {
    return false;
  }
  const inbound = input.messages.filter((message) => message.direction === 'inbound');
  return inbound.length > 0 && inbound.every((message) => message.deletedAt !== null);
}

export function partnershipReplyRefusal(ownerHandle: string): string {
  return `Only @${ownerHandle} can reply on their partnership ad, and that account is not connected to the inbox. Reply on the post instead.`;
}

export const SendInboxReplySchema = z.object({
  text: z.string().trim().min(1).max(INTERCOM_REPLY_MAX_LENGTH),
  takeOver: z.boolean().optional(),
});
export type SendInboxReply = z.infer<typeof SendInboxReplySchema>;

export const SendInboxAttachmentReplySchema = z.object({
  text: z.string().trim().max(INTERCOM_REPLY_MAX_LENGTH).default(''),
  takeOver: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => value === 'true'),
});
export type SendInboxAttachmentReply = z.infer<typeof SendInboxAttachmentReplySchema>;

export const ClaimInboxConversationSchema = z.object({
  takeOver: z.boolean().optional(),
});
export type ClaimInboxConversation = z.infer<typeof ClaimInboxConversationSchema>;

export const InboxOwnershipConflictSchema = z.object({
  message: z.string(),
  owner: z.object({
    adminUserId: z.uuid(),
    email: z.string(),
  }),
});
export type InboxOwnershipConflict = z.infer<typeof InboxOwnershipConflictSchema>;

export const InboxMemberPlanSchema = z.object({
  status: z.string(),
  billingPlan: z.string().nullable(),
  deliveryFrequency: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  nextChargeAt: z.string().nullable(),
});
export type InboxMemberPlan = z.infer<typeof InboxMemberPlanSchema>;

export const InboxMemberOrderSchema = z.object({
  number: z.string().nullable(),
  status: z.string().nullable(),
  parcelStatus: z.string().nullable().default(null),
  placedAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  carrier: z.string().nullable(),
  trackingNumber: z.string().nullable(),
});
export type InboxMemberOrder = z.infer<typeof InboxMemberOrderSchema>;

export const InboxMemberSummarySchema = z.object({
  userId: z.uuid(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  plan: InboxMemberPlanSchema.nullable(),
  nextBoxDate: z.string().nullable(),
  lastOrder: InboxMemberOrderSchema.nullable(),
});
export type InboxMemberSummary = z.infer<typeof InboxMemberSummarySchema>;

export const InboxMemberResponseSchema = z.object({
  member: InboxMemberSummarySchema.nullable(),
  unavailable: z.boolean(),
});
export type InboxMemberResponse = z.infer<typeof InboxMemberResponseSchema>;

export const InboxReadMarkerSchema = z.object({
  conversationId: z.uuid(),
  lastReadAt: z.iso.datetime(),
});
export type InboxReadMarker = z.infer<typeof InboxReadMarkerSchema>;

export const InboxPostPreviewSchema = z.object({
  thumbnailUrl: z.string().nullable(),
});
export type InboxPostPreview = z.infer<typeof InboxPostPreviewSchema>;

export const UpdateInboxStatusSchema = z.object({
  status: z.enum(['needs_reply', 'dismissed']),
});
export type UpdateInboxStatus = z.infer<typeof UpdateInboxStatusSchema>;
